<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-changed="$_queryHandler_hasQueryChanged"
            :query-count="$_queryHandler_numberOfQueries"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input
                v-model="query.searchTerm"
                outlined
                hide-details
                clearable
                @click:append="getRegistrations"
                @keydown.enter="getRegistrations"
              />
            </template>

            <template #default>
              <v-row>
                <v-col cols="6">
                  <v-subheader class="pl-0">Registration Date</v-subheader>
                  <v-row class="mt-0">
                    <v-col cols="6">
                      <datetime-picker
                        v-model="query.params.created_atGreaterThanOrEqualTo"
                        :disabled="isLoading"
                        label="From"
                        dense
                        hide-details
                        outlined
                      />
                    </v-col>
                    <v-col cols="6">
                      <datetime-picker
                        v-model="query.params.created_atLessThanOrEqualTo"
                        :disabled="isLoading"
                        label="To"
                        dense
                        hide-details
                        outlined
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6">
                  <v-subheader class="pl-0">FTD Date</v-subheader>
                  <v-row class="mt-0">
                    <v-col cols="6">
                      <datetime-picker
                        v-model="query.params.ftdDateGreaterThanOrEqualTo"
                        :disabled="isLoading"
                        label="From"
                        clearable
                        dense
                        hide-details
                        outlined
                      />
                    </v-col>
                    <v-col cols="6">
                      <datetime-picker
                        v-model="query.params.ftdDateLessThanOrEqualTo"
                        :disabled="isLoading"
                        label="To"
                        clearable
                        dense
                        hide-details
                        outlined
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" class="pb-0">
                  <v-divider />
                  <v-subheader class="pl-0">Details</v-subheader>
                </v-col>
                <v-col cols="3">
                  <select-list
                    v-model="query.params.countryCode"
                    :items="countries"
                    :clearable="true"
                    :disabled="isLoading"
                    custom-type="country"
                    label="Country"
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col cols="3">
                  <select-list
                    v-model="query.params.partner"
                    :items="mappedIntegratedPartners"
                    :clearable="true"
                    :disabled="isLoading"
                    label="Partner"
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col cols="3">
                  <autocomplete-input
                    v-model="query.params.offer"
                    :items="mappedOffersInRegistrations"
                    :clearable="true"
                    :disabled="isLoading"
                    label="Offer"
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col cols="3">
                  <autocomplete-input
                    v-model="query.params.siteType"
                    :items="siteTypeOptions"
                    :clearable="true"
                    :disabled="isLoading"
                    label="Site Type"
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col cols="3">
                  <autocomplete-input
                    v-model="query.params.site"
                    :items="options.sites"
                    :clearable="true"
                    :disabled="isLoading"
                    label="Site"
                    hide-details
                    outlined
                  />
                </v-col>

                <v-col cols="12" class="pb-0">
                  <v-divider />
                  <v-subheader class="pl-0">Status</v-subheader>
                </v-col>
                <v-col cols="3">
                  <select-list
                    v-model="query.params.successful"
                    :items="successfulOptions"
                    :disabled="isLoading"
                    label="Success status"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col cols="3">
                  <select-list
                    v-model="query.params.stage"
                    :items="recycleStageOptions"
                    :disabled="isLoading"
                    label="Recycling Stage"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col cols="3">
                  <select-list
                    v-model="query.params.ftdStatus"
                    :items="ftdStatusOptions"
                    label="FTD status"
                    :disabled="isLoading"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col cols="3">
                  <select-list
                    v-model="query.params.hasBeenRecycled"
                    :items="hasBeenRecycledOptions"
                    :disabled="isLoading"
                    label="Has Been Recycled"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col class="d-flex align-center" cols="3">
                  <toggle-input
                    v-model="query.params.failedAttempts"
                    :disabled="isLoading"
                    label="Has Failed Attempts"
                    hide-details
                    outlined
                  />
                </v-col>
                <v-spacer />
                <v-col cols="3">
                  <v-btn color="primary" depressed block @click="getRegistrations">Fetch Registrations</v-btn>
                </v-col>
              </v-row>
            </template>
            <template #content>
              <v-container>
                <v-row>
                  <v-col>
                    <v-container>
                      <v-row>
                        <v-col cols="5">
                          <div class="text-h6">
                            <span class="font-weight-bold">{{ pagination.total }}</span> registrations
                            {{ clickIdFilter ? `for click ID` : `for filter options` }}
                          </div>
                          <div class="text-caption">
                            <span class="font-weight-bold">{{ filteredRecycleList.length }}</span>
                            of which are eligible for recycling.
                          </div>
                        </v-col>
                        <v-col cols="7">
                          <template v-if="!!filteredRecycleList.length">
                            <v-container class="px-0">
                              <v-row>
                                <v-col cols="6">
                                  <select-list
                                    v-model="batchRecyclePartner"
                                    :items="mappedIntegratedPartners"
                                    :clearable="true"
                                    :label="`Recycle With Partner`"
                                    dense
                                    hide-details
                                    outlined
                                  />
                                </v-col>
                                <v-col cols="6">
                                  <select-list
                                    v-model="batchRecyclePartnerAccount"
                                    :items="batchRecyclePartnerAccountOptions"
                                    :clearable="false"
                                    :disabled="!batchRecyclePartnerAccountOptions.length"
                                    label="Partner Account"
                                    dense
                                    hide-details
                                    outlined
                                  />
                                </v-col>
                              </v-row>
                            </v-container>
                          </template>
                        </v-col>
                      </v-row>
                      <v-row class="mt-0">
                        <v-col cols="3">
                          <select-list
                            v-model="batchRecycleOption"
                            :items="batchRecycleOptionItems"
                            label="Batch Recycle"
                            outlined
                            hide-details
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip top>
                            <template #activator="{ on }">
                              <div v-on="!!recycleButtonDisabledMessage ? on : null">
                                <v-btn
                                  :disabled="!!recycleButtonDisabledMessage"
                                  color="primary"
                                  depressed
                                  @click="showBatchRecycleConfirmationModal = true"
                                  >Recycle {{ recycleCount }} leads</v-btn
                                >
                              </div>
                            </template>
                            <span>{{ recycleButtonDisabledMessage }}</span>
                          </v-tooltip>
                        </v-col>
                        <v-col cols="auto">
                          <v-btn
                            v-if="batchRecycleOption === 'selected'"
                            color="secondary"
                            :disabled="!recycleList.length"
                            depressed
                            @click="recycleList = []"
                          >
                            Deselect all ({{ recycleList.length }})
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-col>
                </v-row>
              </v-container>
              <v-divider />
              <data-table
                :footer-props="{ 'items-per-page-options': [10, 30, 50, 100, -1] }"
                :headers="headers"
                :items="mappedRegistrations"
                :items-per-page="pagination.perPage"
                :item-class="rowClass($style)"
                :page="pagination.currentPage"
                :server-items-length="pagination.total"
                disable-sort
                no-data-text="No registrations found"
                @pagination="onPaginationUpdate"
                @click-item="onClickEvent"
              >
                <template #header.ftdDate>
                  <v-tooltip max-width="300" bottom>
                    <template #activator="{ attrs, on }">
                      <span class="d-flex align-center" v-bind="attrs" v-on="on">
                        FTD
                        <v-icon class="ml-1" small>help_outline</v-icon>
                      </span>
                    </template>
                    <span>
                      <p>
                        FTDs are updated every 20 minutes using data that exists in Data Studio.
                      </p>
                      <p class="mb-0">
                        "Today" data is updated hourly, and removed at the end of every day. Each day, reports are
                        uploaded for the previous day's data, and if a Partner doesn't report the same data that it did
                        for the day previous, discrepancies can occur.
                      </p>
                    </span>
                  </v-tooltip>
                </template>
                <template #item="{ item }">
                  <td>{{ formattedDate(item.date) }}</td>
                  <td>
                    <div :title="item.email" :class="$style['table-email']">
                      {{ item.email }}
                    </div>
                  </td>
                  <td>
                    {{ formattedOption('countries', item.countryCode, 'value', 'emoji') }}
                    {{ item.countryCode }}
                  </td>
                  <td>{{ item.partner }}</td>
                  <td>{{ item.offer }}</td>
                  <td>
                    <tick-or-cross :value="!!item.successful" />
                    <v-tooltip v-if="!!item.recycledRegistrationId" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          class="ml-2 mr-2"
                          color="success"
                          small
                          v-on="on"
                          @click="clickIdFilter = item.clickId"
                        >
                          autorenew
                        </v-icon>
                      </template>
                      <span>This registration was recycled from registration {{ item.recycledRegistrationId }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="item.successful && item.failedAttempts.length" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span :class="$style.extrainfo" v-bind="attrs" v-on="on"> *</span>
                      </template>
                      <span
                        >Successfully registered with {{ item.partner }} after being rejected by
                        {{ item.failedAttempts.join(', ') }}</span
                      >
                    </v-tooltip>
                  </td>
                  <td>
                    <v-tooltip v-if="item.ftdDate" bottom>
                      <template #activator="{ on }">
                        <v-icon small color="success" v-on="on">check_circle_outline</v-icon>
                      </template>
                      <span>{{ formattedDate(item.ftdDate) }}</span>
                    </v-tooltip>
                    <v-icon v-else small>highlight_off</v-icon>
                  </td>
                  <td>{{ item.stage }}</td>
                  <td v-if="batchRecycleOption === 'selected'" class="px-0">
                    <v-tooltip top>
                      <template #activator="{ on }">
                        <div v-on="!!item.hasBeenRecycled || !!item.ftdDate ? on : null">
                          <v-simple-checkbox
                            :key="item.id"
                            :value="isCheckboxActive(item.id)"
                            :disabled="!!item.hasBeenRecycled || !!item.ftdDate"
                            :ripple="false"
                            color="primary"
                            class="mt-0 pt-0"
                            title="Toggle to recycle"
                            hide-details
                            small
                            @input="onRegistrationRecycleClick(item)"
                          />
                        </div>
                      </template>
                      <div>
                        <span v-if="!!item.hasBeenRecycled">This lead has already been recycled</span>
                        <span v-else>Cannot recycle as FTD is already completed</span>
                      </div>
                    </v-tooltip>
                  </td>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <!-- Batch Recycle Confirmation Modal -->
    <generic-modal
      v-model="showBatchRecycleConfirmationModal"
      :is-loading="isLoading"
      title="Batch Recycle"
      hide-louis
      persistent
      @modal-confirm="onBatchRecycleConfirm"
    >
      <p>
        Are you sure you want to recycle <b>{{ recycleCount }}</b> leads with
        <b>{{ getPartnerNameFromSlug(batchRecyclePartner) }}</b
        >? You must leave this window open until all leads have been recycled.
      </p>
    </generic-modal>

    <!-- Batch Recycling Modal -->
    <generic-modal
      :value="!!batchRecycling"
      confirm-text="Stop Recycling"
      confirm-color="error"
      hide-cancel
      hide-louis
      persistent
      @modal-confirm="stopBatchRecycling"
    >
      <template>
        <div v-for="reg in batchRecyclingRegistrations" :key="reg.id">
          {{
            !reg.hasRecycled && !reg.isRecycling
              ? `Preparing to recycle`
              : reg.isRecycling
              ? 'Recycling'
              : 'Finished recycling'
          }}
          registration (<strong>{{ formattedDate(reg.registration.date) }}</strong
          >, {{ reg.registration.countryCode }}, {{ reg.registration.partner }}) with
          <strong>{{ batchRecyclePartner }}</strong
          ><span v-if="reg.isRecycling">...<v-progress-circular indeterminate size="20"/></span>
          <span v-if="reg.hasRecycled" class="ml-2"><tick-or-cross :value="!!reg.successfullyRecycled"/></span>
        </div>
      </template>
    </generic-modal>

    <!-- Registration Details Modal -->
    <generic-modal
      :value="!!selectedEvent"
      :disabled="recycling || isLoading"
      :persistent="recycling || isLoading"
      confirm-text="Close"
      confirm-color="error"
      title="Event Details"
      hide-cancel
      @modal-confirm="onEventDetailsClose"
      @modal-cancel="onEventDetailsClose"
    >
      <v-container v-if="selectedEvent">
        <v-row>
          <v-col sm="8">
            <div
              v-for="(field, index) in selectedEventFields"
              :key="field.title"
              :class="{
                'grey lighten-2': index % 2 === 0,
              }"
              class="px-1 d-flex justify-space-between"
            >
              <span>{{ field.title }}</span>
              <span
                :class="{
                  'error--text': !field.value || field.value === 'No',
                  'success--text': field.value === 'Yes',
                  'text-uppercase': !field.value || field.value === 'No',
                }"
                style="max-width: 70%"
                v-html="field.value || 'NOT SET'"
              >
              </span>
            </div>
          </v-col>
        </v-row>
      </v-container>
      <v-container v-if="selectedEvent && !selectedEvent.ftdDate && !selectedEvent.hasBeenRecycled && !recycling">
        <v-row>
          <v-col sm="8">
            <v-row>
              <v-col>
                <autocomplete-input
                  v-model="recyclePartner"
                  :items="mappedIntegratedPartners"
                  :clearable="true"
                  label="Recycle With Partner"
                  hide-details
                />
              </v-col>
              <v-col>
                <autocomplete-input
                  v-model="recyclePartnerAccount"
                  :items="recyclePartnerAccounts"
                  :clearable="false"
                  :disabled="!recyclePartnerAccounts.length"
                  label="Partner Account"
                  hide-details
                />
              </v-col>
              <v-col cols="12">
                <v-btn
                  :disabled="!recyclePartner || !isPermittedTo('update')"
                  color="primary"
                  block
                  depressed
                  @click="recycleRegistration"
                  >Recycle</v-btn
                >
              </v-col>
            </v-row>
          </v-col>
          <v-col sm="8">
            <v-row>
              <v-col v-if="recycling" cols="12">Recycling Registration...</v-col>
              <v-col v-if="selectedEvent && selectedEvent.hasBeenRecycled" cols="12">
                This registration has already been recycled
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </generic-modal>

    <v-fab-transition>
      <v-btn
        v-show="isPermittedTo('update') && mappedRegistrations.length"
        color="primary"
        :disabled="isLoading || !mappedRegistrations.length"
        fab
        dark
        fixed
        bottom
        right
        title="Export as CSV"
        @click="onClickExport"
      >
        <v-icon>file_download</v-icon>
      </v-btn>
    </v-fab-transition>
  </page-loader>
</template>

<script>
// Helpers
import { mapGetters, mapMutations, mapState } from 'vuex'
import { set as setDate } from 'date-fns'
import moment from 'moment'
import isSet from '@/utils/is-set'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import DataTable from '@/components/data-table'
import DatetimePicker from '@/components/datetime-picker'
import FilterAccordion from '@/components/filter-accordion'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
import ToggleInput from '@/components/forms/toggle-input'
import TickOrCross from '@/components/tick-or-cross'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { offers, partners, registrations } from '@/services/offers'

export default {
  name: 'RegistrationsReport',
  components: {
    AutocompleteInput,
    DataTable,
    DatetimePicker,
    FilterAccordion,
    GenericModal,
    PageLoader,
    SearchInput,
    SelectList,
    TickOrCross,
    ToggleInput,
  },
  mixins: [QueryParamsHandler, PermissionsHandler],
  data: () => ({
    batchRecyclePartner: null,
    batchRecyclePartnerAccount: null,
    batchRecycling: false,
    batchRecyclingRegistrations: [],
    batchRecycleOption: 'all',
    batchRecycleOptionItems: [
      { key: 'All', value: 'all' },
      { key: 'Selected only', value: 'selected' },
    ],
    clickIdFilter: null,
    defaultQuery: {
      created_atLessThanOrEqualTo: setDate(new Date(), { hours: 23, minutes: 59, seconds: 59 }),
      created_atGreaterThanOrEqualTo: setDate(new Date(), { hours: 0, minutes: 0, seconds: 0 }),
    },
    ftdStatusOptions: [
      { key: 'FTD', value: true },
      { key: 'Lead', value: false },
    ],
    filterDrawerOpen: false,
    filteredRecycleList: [],
    hasBeenRecycledOptions: [
      { key: 'Already recycled', value: true },
      { key: 'Never been recycled', value: false },
    ],
    isLoading: false,
    offers: [],
    integratedPartners: [],
    pagination: { perPage: 30 },
    partners: [],
    queryDefaults: ['created_atLessThanOrEqualTo', 'created_atGreaterThanOrEqualTo'],
    queryTypes: {
      created_atLessThanOrEqualTo: 'date',
      created_atGreaterThanOrEqualTo: 'date',
      ftdDateLessThanOrEqualTo: 'date',
      ftdDateGreaterThanOrEqualTo: 'date',
      stage: 'number',
    },
    queriesDefined: [
      'created_atLessThanOrEqualTo',
      'created_atGreaterThanOrEqualTo',
      'failedAttempts',
      'ftdDateLessThanOrEqualTo',
      'ftdDateGreaterThanOrEqualTo',
      'countryCode',
      'ftdStatus',
      'partner',
      'offer',
      'siteType',
      'successful',
      'stage',
      'hasBeenRecycled',
      'site',
    ],
    recycleList: [],
    recyclePartnerAccount: null,
    recyclePartner: null,
    recycleStageOptions: [
      { key: 'Stage 1', value: 1 },
      { key: 'Stage 2', value: 2 },
      { key: 'Stage 3', value: 3 },
      { key: 'Stage 4', value: 4 },
      { key: 'Stage 5', value: 5 },
    ],
    recycling: false,
    registrations: [],
    selectedEvent: null,
    showBatchRecycleConfirmationModal: false,
    successfulOptions: [
      { key: 'Successful', value: true },
      { key: 'Failed', value: false },
    ],
  }),
  computed: {
    ...mapState('account', ['user']),
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['options', 'formattedOption', 'hasOptions', 'getItemsForOption']),
    batchRecyclePartnerAccountOptions() {
      if (!this.batchRecyclePartner) return []
      const found = this.integratedPartners.find(i => i.slug === this.batchRecyclePartner)
      return (found && Array.isArray(found.accounts) && found.accounts) || []
    },
    formattedFilters() {
      const {
        countryCode,
        created_atGreaterThanOrEqualTo,
        created_atLessThanOrEqualTo,
        failedAttempts,
        ftdDateGreaterThanOrEqualTo,
        ftdDateLessThanOrEqualTo,
        ftdStatus,
        hasBeenRecycled,
        offer,
        partner,
        stage,
        siteType,
        successful,
        site,
      } = this.query.params

      return this.clickIdFilter
        ? {
            clickId: this.clickIdFilter,
          }
        : {
            created_atGreaterThanOrEqualTo,
            created_atLessThanOrEqualTo,
            countryCode,
            ftdDateGreaterThanOrEqualTo,
            ftdDateLessThanOrEqualTo,
            ftdDateIsSet: ftdStatus || undefined,
            ftdDateNotSet: ftdStatus === false || undefined,
            hasBeenRecycled,
            responseNotEqualTo: failedAttempts ? '[]' : undefined,
            partner,
            offer,
            siteType, // Custom Filter
            stage,
            successful,
            site,
          }
    },
    recyclePartnerAccounts() {
      if (!this.recyclePartner) return []
      const found = this.integratedPartners.find(i => i.slug === this.recyclePartner)
      return (found && Array.isArray(found.accounts) && found.accounts) || []
    },
    countries() {
      return this.getItemsForOption('countries')
    },
    hasQueryChanged() {
      return (
        Object.entries(this.query.params).some(([param, value]) => {
          switch (param) {
            case 'created_atLessThanOrEqualTo':
              return String(value) !== String(setDate(new Date(), { hours: 23, minutes: 59, seconds: 59 }))
            case 'created_atGreaterThanOrEqualTo':
              return String(value) !== String(setDate(new Date(), { hours: 0, minutes: 0, seconds: 0 }))
            default:
              return isSet(value)
          }
        }) || !!this.query.searchTerm
      )
    },
    headers() {
      return [
        { text: 'Registration Date', value: 'date', csvValue: 'date', csvType: 'date' },
        { text: 'Email', value: 'email', csvValue: 'unmaskedEmail' },
        { text: 'Country', value: 'countryCode', csvValue: 'countryCode' },
        { text: 'Partner', value: 'partner', csvValue: 'partner' },
        { text: 'Offer', value: 'offer', csvValue: 'offer' },
        { text: 'Successful', value: 'successful', csvValue: 'successful', csvType: 'boolean' },
        { text: 'FTD', value: 'ftdDate', csvValue: 'ftdDate', csvType: 'date' },
        { text: 'Stage', value: 'stage', csvValue: 'stage' },
        this.batchRecycleOption === 'selected' ? { text: '', value: 'recycle' } : null,
      ].filter(Boolean)
    },
    recycleButtonDisabledMessage() {
      if (!this.isPermittedTo('update')) return 'User does not have permision to recycle leads'
      if (!this.batchRecyclePartner) return 'A partner must be selected in order to recycle leads'
      if (!this.recycleCount) return 'At least one registration must be selected in order to recycle leads'
      return null
    },
    canViewUnmaskedDetails() {
      if (!this.user || !this.user.console_roles) return false
      return this.user.console_roles.includes('commercial-admin')
    },
    mappedOffersInRegistrations() {
      return this.registrations.reduce((acc, r) => {
        const duplicate = acc.find(({ value }) => value === r.offer)
        if (!duplicate) {
          acc.push({
            key: this.getOfferNameFromSlug(r.offer),
            value: r.offer,
          })
        }
        return acc
      }, [])
    },
    mappedRegistrations() {
      return this.registrations.map(
        ({
          id,
          clickId,
          countryCode,
          created_at,
          email,
          firstName,
          lastName,
          offer,
          partner,
          stage,
          successful,
          response,
          hasBeenRecycled,
          automatic,
          recycledRegistrationId,
          ftdDate,
          ftdPartner,
          ftdCommission,
          referringHostname,
          hostname,
        }) => ({
          id,
          clickId,
          countryCode,
          date: created_at,
          email: this.canViewUnmaskedDetails ? email : this.maskEmail(email),
          unmaskedEmail: email,
          name: this.formatName(firstName, lastName),
          partner: this.getPartnerNameFromSlug(partner),
          offer: this.getOfferNameFromSlug(offer),
          successful,
          stage,
          response,
          isTest:
            email.includes('test') ||
            firstName.toLowerCase().includes('test') ||
            lastName.toLowerCase().includes('test'),
          failedAttempts: this.parseResponse(response).map(r => this.getPartnerNameFromSlug(r.partner)),
          hasBeenRecycled,
          automatic,
          recycledRegistrationId,
          ftdDate,
          ftdPartner,
          ftdCommission,
          partnerSlug: partner,
          offerSlug: offer,
          referringHostname,
          hostname,
        })
      )
    },
    numberOfQueries() {
      const additionalQueries = this.clickIdFilter ? 1 : 0
      return Number(this.$_queryHandler_numberOfQueries) + additionalQueries
    },
    selectedEventFields() {
      if (!this.selectedEvent) return []
      const {
        id,
        clickId,
        countryCode,
        date,
        email,
        name,
        offer,
        partner,
        source,
        successful,
        response,
        ftdDate,
        ftdPartner,
        ftdCommission,
        hostname,
        referringHostname,
      } = this.selectedEvent
      return [
        { title: 'Registration ID', value: id },
        { title: 'Date', value: moment(date).format('DD/MM/YYYY HH:mm:ss') },
        { title: 'Name', value: name },
        { title: 'Email', value: email },
        { title: 'Partner', value: this.getPartnerNameFromSlug(partner) },
        { title: 'Offer', value: this.getOfferNameFromSlug(offer) },
        { title: 'Click ID', value: clickId },
        { title: 'Country', value: this.formattedOption('countries', countryCode) },
        {
          title: 'Successful',
          value: successful ? 'Yes' : 'No',
        },
        { title: 'Responses', value: this.getErrorFromResponse(response, successful, partner) },
        { title: ftdDate ? 'FTD Date' : 'FTD', value: ftdDate ? moment(ftdDate).format('DD/MM/YYYY HH:mm:ss') : 'No' },
        { title: 'Hostname', value: hostname },
        { title: 'Referring Hostname', value: referringHostname },
        ...[
          { title: 'FTD Partner', value: ftdPartner },
          {
            title: 'FTD Commission',
            value: new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(ftdCommission / 100),
          },
        ].filter(f => !!ftdDate),
      ].filter(Boolean)
    },
    siteTypeOptions() {
      return this.getItemsForOption('siteTypes').filter(({ value }) => value)
    },
    mappedOffers() {
      return this.offers.map(({ name, slug, type }) => ({
        key: `${name} (${this.formattedOption('offerTypes', type)})` || slug,
        value: slug,
      }))
    },
    mappedPartners() {
      return this.partners.map(({ name, slug }) => ({
        key: name || slug,
        value: slug,
      }))
    },
    mappedIntegratedPartners() {
      return this.integratedPartners
        .filter(p => p.methods.includes('registerLead'))
        .map(p => ({
          key: p.name,
          value: p.slug,
        }))
    },
    recycleCount() {
      return this.batchRecycleOption === 'selected' ? this.recycleList.length : this.filteredRecycleList.length
    },
  },
  watch: {
    clickIdFilter: {
      handler(clickId) {
        this.clearFilters()
        if (clickId) {
          this.query.params.created_atLessThanOrEqualTo = undefined
          this.query.params.created_atGreaterThanOrEqualTo = undefined
        }
        this.getRegistrations()
      },
    },
    batchRecycleOption() {
      this.recycleList = []
    },
    batchRecyclePartner() {
      this.batchRecyclePartnerAccount = null
    },
    recyclePartner() {
      this.recyclePartnerAccount = null
    },
  },
  async created() {
    await Promise.all([this.getOffersAndPartners(), this.getIntegratedPartners(), this.getRegistrations()])
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    clearFilters() {
      Object.keys(this.query.params).forEach(param => {
        switch (param) {
          case 'created_atLessThanOrEqualTo':
            this.$set(
              this.query.params,
              'created_atLessThanOrEqualTo',
              setDate(new Date(), { hours: 23, minutes: 59, seconds: 59 })
            )
            break
          case 'created_atGreaterThanOrEqualTo':
            this.$set(
              this.query.params,
              'created_atGreaterThanOrEqualTo',
              setDate(new Date(), { hours: 0, minutes: 0, seconds: 0 })
            )
            break
          default:
            this.$set(this.query.params, param, undefined)
        }
      })
      this.query.searchTerm = ''
    },
    formattedDate(date) {
      return moment(date).format('DD/MM/YYYY HH:mm')
    },
    isRecycleBoxActive(item) {
      if (!this.recycleList.length || this.batchRecycleOption === 'all') return false
      return this.recycleList.find(({ id }) => id === item.id)
    },
    parseResponse(res) {
      try {
        const parsed = JSON.parse(res)
        return Array.isArray(parsed) ? parsed : []
      } catch (err) {
        return []
      }
    },
    getErrorFromResponse(res, successful, partner) {
      try {
        const parsed = JSON.parse(res)
        if (Array.isArray(parsed) && parsed.every(r => r.partner && r.hasOwnProperty('response'))) {
          const attempts = parsed.map(
            p => `<strong>Failed attempt with ${p.partner}</strong>: ${JSON.stringify(p.response)}`
          )

          if (!!successful) {
            attempts.push(`<strong>Successfully registered with ${partner}</strong>`)
          }
          return attempts.join('<br>')
        }
        return parsed
      } catch (err) {
        return String(res)
      }
    },
    formatName(forename, surname) {
      const first = forename.charAt(0).toUpperCase() + forename.substring(1)
      const initial = surname.charAt(0).toUpperCase()
      return `${first} ${initial}.`
    },
    maskEmail(email) {
      const [identifier, provider] = email.split('@')
      const first = identifier[0]
      const last = identifier[identifier.length - 1]
      const pad = ''.padStart(identifier.length - 2, '*')
      return `${first}${pad}${last}@${provider}`
    },
    getPartnerNameFromSlug(slug) {
      const found = this.mappedPartners.find(partner => partner.value === slug)
      return found ? found.key : slug
    },
    getOfferNameFromSlug(slug) {
      const found = this.mappedOffers.find(offer => offer.value === slug)
      return found ? found.key : slug
    },
    async recycleRegistration() {
      if (!confirm(`Are you sure you want to recycle this registration with ${this.recyclePartner}?`)) {
        return false
      }
      this.recycling = true
      try {
        await registrations.recycleRegistration(this.selectedEvent.id, {
          partner: this.recyclePartner,
          partnerAccount: this.recyclePartnerAccount || undefined,
          fallbackOnFail: false,
        })
        this.$router.go()
      } catch (err) {
        this.recycling = false
        alert(`Error: ${err.message}`)
      }
    },
    async stopBatchRecycling() {
      this.batchRecycling = false
      this.getRegistrations()
    },
    async batchRecycleRegistrations() {
      this.batchRecycling = true
      this.batchRecyclingRegistrations = this.recycleList.map(r => ({
        registration: r,
        isRecycling: false,
        hasRecycled: false,
        successfullyRecycled: false,
      }))
      for (const reg of this.batchRecyclingRegistrations) {
        if (!this.batchRecycling) {
          break
        }
        reg.isRecycling = true
        try {
          await registrations.recycleRegistration(reg.registration.id, {
            partner: this.batchRecyclePartner,
            partnerAccount: this.batchRecyclePartnerAccount || undefined,
            fallbackOnFail: false,
          })
          reg.successfullyRecycled = true
        } catch (err) {
          reg.successfullyRecycled = false
        }
        reg.isRecycling = false
        reg.hasRecycled = true
      }
      this.batchRecycling = false
      this.$notify({
        type: 'success',
        text: 'Batch Recycling has finished',
        duration: -1,
      })
      this.recycleList = []
      this.getRegistrations()
    },
    async getRegistrations() {
      this.isLoading = true

      const { perPage, currentPage: page } = this.pagination
      const { searchTerm: search } = this.query

      try {
        const { registrations: data, pagination: { total } = {}, recyclable } = await registrations.getRegistrations({
          filters: this.formattedFilters,
          perPage: perPage !== -1 ? perPage : null,
          page: perPage !== -1 ? page : 1,
          search,
          includeRecyclable: true,
        })
        this.pagination.total = total
        this.registrations = data
        this.filteredRecycleList = recyclable
      } catch (error) {
        console.error(error)
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve registrations',
        })
      }

      this.isLoading = false
    },
    async getOffersAndPartners() {
      try {
        const { data: offerRows } = await offers.getAllOffers({ compact: true })
        this.offers = offerRows
        const { data: partnerRows } = await partners.getAllPartners({
          compact: true,
          filters: { archivedContains: [true, false] },
        })
        this.partners = partnerRows
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Offers & Partners',
        })
      }
    },
    async getIntegratedPartners() {
      try {
        this.integratedPartners = await partners.getIntegratedPartners()
      } catch (err) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve integrated partners',
        })
      }
    },
    onClickEvent(event) {
      this.selectedEvent = event
    },
    rowClass($style) {
      return item => [
        item.isTest ? $style['test-registration'] : '',
        item.hasBeenRecycled ? $style['recycled-registration'] : '',
        this.isRecycleBoxActive(item) ? $style['toggled-registration'] : '',
      ]
    },
    async onClickExport() {
      this.isLoading = true
      try {
        const { countryCode, partner, offer } = this.query.params
        const filename = ['registrations', countryCode, partner, offer].filter(Boolean).join('-')
        await registrations.exportCsv({ filters: this.formattedFilters }, filename)
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to export registrations as CSV',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    isCheckboxActive(id) {
      return !!this.recycleList.find(reg => reg.id === id)
    },
    onRegistrationRecycleClick(reg) {
      const regIndex = this.recycleList.findIndex(({ id }) => reg.id === id)
      if (regIndex === -1) {
        this.recycleList.push(reg)
      } else {
        this.recycleList.splice(regIndex, 1)
      }
    },
    async onBatchRecycleConfirm() {
      if (this.batchRecycleOption === 'all') this.recycleList = this.filteredRecycleList
      this.showBatchRecycleConfirmationModal = false
      this.batchRecycleRegistrations()
    },
    onEventDetailsClose() {
      this.selectedEvent = null
      this.recyclePartner = null
      this.recyclePartnerAccount = null
    },
    async onPaginationUpdate(pagination) {
      this.$_queryHandler_onUpdatePagination(pagination)
      this.getRegistrations()
    },
  },
}
</script>

<style lang="scss" module>
.tooltip {
  word-break: break-all;
}
.test-registration {
  opacity: 0.25;
}
.recycled-registration {
  background-color: rgba(#ccc, 0.2);
}
.toggled-registration {
  background-color: rgba(#169c78, 0.1);
}
.extrainfo {
  font-size: 1.5em;
}

.table-email {
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
